import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { kindToSection } from "../lib/builder-helpers";
import { useApi } from "../hooks/useApi";
import { SECTIONS, type Pkg } from "../pages/builder";
import type { CatalogItem, OfferSection } from "./types";
import { Button, Drawer, EmptyState, Icon, Input, LoadingRows, Modal, Select, Checkbox, Textarea } from "./components";
import { money } from "./format";
import { CatalogPickerRow } from "./CatalogPickerRow";
import { PackageTile } from "./PackageTile";

/** Ergebnis eines KI-Freitext-Imports (`onParseFreeform`) — dieselbe Form wie bisher
 *  `importResult` in `builder.tsx`, jetzt hier benannt, weil sowohl diese Komponente (lokaler
 *  State) als auch `builder.tsx` (Rueckgabetyp von `parseFreeform`) sie brauchen. */
export interface ImportResult {
  count: number;
  computedTotalCents: number | null;
  statedTotalCents: number | null;
  flags: { message: string }[];
  warning: string | null;
}

/**
 * „Position hinzufügen"-Drawer (Region 6) — Katalog/KI-Text/Manuell-Tabs. Aus `builder.tsx`
 * (Schritt 7, Einheit B, Konzept § 2.9/§ 4.7).
 *
 * Phase B (Konzept § 2.9/§ 3): `positionDrawerTab`/`catalogSearch`/`catalogCardFilter`/
 * `importText`/`importBusy`/`importResult` sind reiner Drawer-UI-State (Konzept § 3 Tabelle
 * Zeile 3) und vollstaendig hierher gewandert. `positionDrawerOpen`/`positionDrawerSection`
 * bleiben laut Konzept beim Parent (`builder.tsx`) — Sektions-Kopfzeilen und `VariantenLeiste`
 * oeffnen den Drawer von aussen mit einer bestimmten Sektion.
 *
 * Kein Ref-Begleiter zu einem der sechs Zustaende (§ 6.2 greift nicht). Kein H-12-Gate wandert
 * hierher: `addFreetext`/`addFromCatalog` tragen ihr eigenes H-12-Gate und bleiben komplett beim
 * Parent (analog `applyPackage` aus Schritt 6) — sie schreiben in `items`, fachlicher State
 * (Konzept § 3 Tabelle Zeile 1). `parseFreeform` traegt ebenfalls sein H-12-Gate und bleibt aus
 * demselben Grund beim Parent; nur sein Ergebnis (`importBusy`/`importResult`) ist jetzt lokal —
 * `handleParse` unten setzt/loescht `importBusy` in einem `try {...} finally {...}` um den
 * `await onParseFreeform(...)`-Aufruf (exaktes Muster von `PaketUebernehmenPanel.handleApply`,
 * Schritt 6) und uebernimmt das Ergebnis in `importResult` nur bei Erfolg (bei einem H-12-Abbruch
 * oder Fehler liefert `onParseFreeform` `null`, `importResult` bleibt dann unveraendert — exakt
 * das bisherige Verhalten, als `parseFreeform` bei Fehlschlag `importResult` gar nicht anfasste).
 *
 * `filteredCatalog` (Katalog-Suche + Karten-Filter) haengt an `catalogSearch`/
 * `catalogCardFilter` und musste darum mitwandern (analog `offeneRueckfragen` in Schritt 5, das
 * mit `beantworteteFragen` mitzog) — die Komponente bekommt stattdessen die ROHEN Katalog-Daten
 * (`catalogItems`) und `builderCardByCategory` als Props.
 *
 * `positionDrawerTab` bekommt keinen Prop-Ersatz: alle drei Aufrufstellen von
 * `openPositionDrawer()` in `builder.tsx` oeffneten den Drawer schon bisher IMMER auf dem
 * Katalog-Reiter (der `tab`-Parameter dort war zwar vorhanden, aber nie mit einem anderen Wert
 * belegt). Der `useEffect` unten reproduziert das: bei jedem Uebergang `open: false -> true`
 * (die einzige erreichbare Oeffnungs-Situation — der Drawer haengt hinter einem Vollflaechen-
 * Backdrop, das jede andere Oeffnungs-Aktion waehrenddessen blockiert) setzt er den Reiter auf
 * "katalog" zurueck, genau wie zuvor `setPositionDrawerTab(tab)` in `openPositionDrawer()`.
 */
export function PositionHinzufuegenDrawer({
  open,
  onClose,
  section,
  onSectionChange,
  catalogItems,
  catalogLoading,
  activeBuilderCards,
  builderCardByCategory,
  builderCardActiveById,
  catalogCategories = [],
  packages = [],
  onApplyPackage,
  offeneVariante = null,
  onApplyPackageInAktive,
  onAddFromCatalog,
  onParseFreeform,
  onAddFreetext,
}: {
  open: boolean;
  onClose: () => void;
  section: OfferSection;
  onSectionChange: (s: OfferSection) => void;
  catalogItems: CatalogItem[];
  catalogLoading: boolean;
  activeBuilderCards: { id: string; name: string; active: boolean }[];
  builderCardByCategory: Map<string, string | null>;
  /** Migration 391: `menu_cards.active` je Karten-Id - Positionen einer deaktivierten Karte
   *  fallen aus dem Katalog-Picker (unabhaengig vom gewaehlten Karten-Filter). */
  builderCardActiveById: Map<string, boolean>;
  /** Eigene Katalog-Kategorien des Betriebs (`/api/catalog/categories`, Reihenfolge der API) -
   *  zweiter Filter „Alle Kategorien" neben dem Karten-Filter (Antoine, 12.10.2026). */
  catalogCategories?: { id: string; name: string }[];
  /** Pakete/Menues (`/api/packages`) - Chip „Pakete & Menüs", Uebernahme ueber `applyPackage()`
   *  im Builder (neue Variante, Betraege server-seitig). */
  packages?: Pkg[];
  onApplyPackage?: (pkg: Pkg) => void | Promise<void>;
  /** Punkt C (Antoine 13.10.2026, „Nachfragen"): Name der offenen (geladenen) Variante. Ist sie
   *  gesetzt, fragt ein Paket-Klick „in offene Variante oder neue Variante?". */
  offeneVariante?: string | null;
  onApplyPackageInAktive?: (pkg: Pkg) => void | Promise<void>;
  onAddFromCatalog: (ci: CatalogItem, qty?: number) => void;
  onParseFreeform: (text: string) => Promise<ImportResult | null>;
  onAddFreetext: (section: OfferSection) => void;
}) {
  const [tab, setTab] = useState<"katalog" | "ki" | "manuell">("katalog");
  const [catalogSearch, setCatalogSearch] = useState("");
  // P3 (Speisekarten): Karten-Filter im Katalog-Picker; inaktive Karten sind ausgeblendet (die
  // API liefert nur nicht-archivierte, `active` filtert der Parent vor der Uebergabe).
  const [catalogCardFilter, setCatalogCardFilter] = useState<string>("all");
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState<string>("all");
  const [paketeAnsicht, setPaketeAnsicht] = useState(false);
  const [paketFrage, setPaketFrage] = useState<Pkg | null>(null);
  // Analog `catalog.tsx` ("Archivierte zeigen"): archivierte Positionen bleiben standardmaessig
  // ausgeblendet (die meisten sind bewusst stillgelegt), sind aber ueber diesen Schalter
  // durchsuchbar -- eine Katalog-Kategorie kann archiviert werden (Reimport, Aufraeumen), ohne
  // dass ihre Positionen dadurch aus dem Angebot-Editor spurlos verschwinden (Storia-Befund:
  // "Aperitivo & Fingerfood" archiviert seit 27.07.2026, dadurch komplett unauffindbar).
  //
  // Bewusst EIN EIGENER, erst bei Aktivierung ausgeloester Fetch statt den `catalogItems`-Prop
  // (`builder.tsx`, `/api/catalog/items` ohne Parameter) zu erweitern: Diese Query wird an
  // etlichen weiteren Stellen der Seite verwendet (LMIV-Nachschlagewerk, Positionsliste) und ist
  // in gut zwei Dutzend Web-Tests unter genau diesem Pfad-String gemockt -- eine Aenderung DORT
  // waere nicht minimal-invasiv. `includeArchived=true` liefert Aktive+Archivierte zusammen
  // (dieselbe Route/derselbe Parameter wie in der Katalogverwaltung), darum reicht es, bei
  // aktiviertem Schalter komplett auf dieses breitere Ergebnis umzuschwenken; bis es da ist,
  // bleibt der (bereits geladene) aktive Bestand sichtbar statt kurz leerzulaufen.
  const [showArchived, setShowArchived] = useState(false);
  const archivedCatalogQ = useApi<CatalogItem[]>(showArchived ? "/api/catalog/items?includeArchived=true" : null);
  const effectiveCatalog = showArchived && archivedCatalogQ.data ? archivedCatalogQ.data : catalogItems;
  const [importText, setImportText] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  useEffect(() => {
    if (open) {
      setTab("katalog");
      setPaketeAnsicht(false);
    }
  }, [open]);

  /** Katalog-Tab des Positions-Drawers (8c): Suche + auf die gewaehlte Sektion vorgefiltert. */
  const sectionCatalog = useMemo(() => {
    return effectiveCatalog
      .filter((ci) => kindToSection(ci.kind) === section)
      // Migration 391: Positionen einer deaktivierten Karte verschwinden aus dem Picker -
      // unabhaengig vom gewaehlten Karten-Filter unten. Positionen ohne Karten-Zuordnung
      // (Equipment/Personal/Sonstiges) sind davon nie betroffen.
      .filter((ci) => {
        const cardId = ci.categoryId ? builderCardByCategory.get(ci.categoryId) ?? null : null;
        if (cardId == null) return true;
        return builderCardActiveById.get(cardId) !== false;
      })
      .filter((ci) => {
        // P3: Karten-Filter (Kategorie -> Karte); "none" = Positionen ohne Karten-Zuordnung.
        if (catalogCardFilter === "all") return true;
        const cardId = ci.categoryId ? builderCardByCategory.get(ci.categoryId) ?? null : null;
        return catalogCardFilter === "none" ? cardId == null : cardId === catalogCardFilter;
      });
  }, [effectiveCatalog, section, catalogCardFilter, builderCardByCategory, builderCardActiveById]);

  // Nur Kategorien anbieten, die in der gewaehlten Sektion/Karte ueberhaupt Positionen haben;
  // passt die gewaehlte nach einem Sektionswechsel nicht mehr, gilt wieder „Alle Kategorien".
  // Punkt C (Antoine 13.10.2026, „Ja, anzeigen"): Positionen ohne (bekannte) Kategorie bekommen
  // eine eigene Gruppe „Ohne Kategorie" - sonst waeren sie ueber den Filter nicht auffindbar.
  const ohneKategorie = useMemo(() => {
    const bekannt = new Set(catalogCategories.map((c) => c.id));
    return (ci: CatalogItem) => !ci.categoryId || !bekannt.has(ci.categoryId);
  }, [catalogCategories]);
  const categoryOptions = useMemo(() => {
    const vorhanden = new Set(sectionCatalog.map((ci) => ci.categoryId));
    const eigene = catalogCategories.filter((c) => vorhanden.has(c.id));
    return sectionCatalog.some(ohneKategorie) ? [...eigene, { id: "none", name: "Ohne Kategorie" }] : eigene;
  }, [sectionCatalog, catalogCategories, ohneKategorie]);
  const categoryFilter = categoryOptions.some((c) => c.id === catalogCategoryFilter) ? catalogCategoryFilter : "all";

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return sectionCatalog
      .filter((ci) => categoryFilter === "all" || (categoryFilter === "none" ? ohneKategorie(ci) : ci.categoryId === categoryFilter))
      .filter((ci) => (q ? ci.name.toLowerCase().includes(q) || (ci.description ?? "").toLowerCase().includes(q) : true));
  }, [sectionCatalog, catalogSearch, categoryFilter, ohneKategorie]);

  const sichtbarePakete = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return packages
      .filter((p) => !p.archivedAt)
      .filter((p) => (q ? p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q) : true));
  }, [packages, catalogSearch]);
  const paketeWaehlbar = onApplyPackage != null && packages.some((p) => !p.archivedAt);

  async function handleParse() {
    setImportBusy(true);
    try {
      const result = await onParseFreeform(importText);
      if (result) setImportResult(result);
    } finally {
      setImportBusy(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Position hinzufügen"
      subtitle={`Sektion: ${SECTIONS.find((s) => s.key === section)?.label ?? ""}`}
    >
      <div className="space-y-4">
        <div className="flex gap-1 rounded-lg bg-surface-container-high p-1 dark:bg-surface-variant/20">
          {(
            [
              { key: "katalog", label: "Katalog" },
              { key: "ki", label: "KI-Text" },
              { key: "manuell", label: "Manuell" },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 rounded-md px-3 py-1.5 font-label-md text-label-md transition-colors ${
                tab === t.key
                  ? "bg-surface-container-lowest text-primary shadow-sm dark:bg-night-card"
                  : "text-on-surface-variant dark:text-secondary-fixed-dim"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "katalog" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {[
                ...SECTIONS,
                // Pakete/Menues als zusaetzlicher Chip (Antoine, 12.10.2026); die festen bleiben.
                ...(paketeWaehlbar ? [{ key: "pakete" as const, label: "Pakete & Menüs", icon: "inventory_2" }] : []),
              ].map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    if (s.key === "pakete") return setPaketeAnsicht(true);
                    setPaketeAnsicht(false);
                    onSectionChange(s.key);
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-label-md text-label-md transition-colors ${
                    (paketeAnsicht ? s.key === "pakete" : section === s.key)
                      ? "bg-primary text-on-primary"
                      : "bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest dark:bg-white/[0.08] dark:text-secondary-fixed-dim"
                  }`}
                >
                  <Icon name={s.icon} className="text-[14px]" /> {s.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="group relative flex-1">
                <Icon
                  name="search"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant transition-colors group-focus-within:text-primary dark:text-secondary-fixed-dim"
                />
                <Input className="!pl-10" placeholder="Suchen …" value={catalogSearch} onChange={(e) => setCatalogSearch(e.target.value)} />
              </div>
              {/* P3: nach Karte filtern (nur aktive Karten). */}
              {!paketeAnsicht && activeBuilderCards.length > 0 && (
                <div className="max-w-[180px]">
                  <Select
                    value={catalogCardFilter}
                    onValueChange={setCatalogCardFilter}
                    options={[
                      { value: "all", label: "Alle Karten" },
                      { value: "none", label: "Ohne Karte" },
                      ...activeBuilderCards.map((k) => ({ value: k.id, label: k.name })),
                    ]}
                  />
                </div>
              )}
              {/* Eigene Katalog-Kategorien des Betriebs (Antoine, 12.10.2026). */}
              {!paketeAnsicht && categoryOptions.some((c) => c.id !== "none") && (
                <div className="max-w-[180px]">
                  <Select
                    ariaLabel="Kategorie"
                    value={categoryFilter}
                    onValueChange={setCatalogCategoryFilter}
                    options={[{ value: "all", label: "Alle Kategorien" }, ...categoryOptions.map((c) => ({ value: c.id, label: c.name }))]}
                  />
                </div>
              )}
            </div>
            {paketeAnsicht ? (
              sichtbarePakete.length === 0 ? (
                <EmptyState icon="inventory_2" title="Nichts gefunden" hint="Suche anpassen." />
              ) : (
                <div className="space-y-2">
                  <p className="flex items-center gap-1.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="info" className="text-[16px]" />
                    {offeneVariante && onApplyPackageInAktive
                      ? "Sie wählen danach: in die offene Variante oder als neue Variante."
                      : "Ein Paket oder Menü wird als neue Angebotsvariante übernommen."}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {sichtbarePakete.map((p) => (
                      <PackageTile
                        key={p.id}
                        pkg={p}
                        busy={false}
                        onApply={() => {
                          if (offeneVariante && onApplyPackageInAktive) return setPaketFrage(p);
                          void onApplyPackage?.(p);
                          onClose();
                        }}
                      />
                    ))}
                  </div>
                </div>
              )
            ) : (
            <>
            {/* Analog `catalog.tsx` ("Archivierte zeigen"): archivierte Kategorien/Positionen
                (z. B. durch einen Speisekarten-Reimport stillgelegt) bleiben so auffindbar, ohne
                den Katalog-Tab standardmaessig mit stillgelegtem Altbestand zu fluten. */}
            <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Auch archivierte Positionen durchsuchen
            </label>
            {catalogLoading ? (
              <LoadingRows />
            ) : filteredCatalog.length === 0 ? (
              <EmptyState
                icon="menu_book"
                title="Nichts gefunden"
                hint={
                  catalogSearch
                    ? showArchived
                      ? "Suche anpassen."
                      : 'Suche anpassen – oder „Auch archivierte Positionen durchsuchen" aktivieren.'
                    : "Legen Sie zuerst Positionen im Katalog an – oder importieren Sie eine Speisekarte."
                }
              />
            ) : (
              <div className="space-y-1">
                {filteredCatalog.map((ci) => (
                  <CatalogPickerRow key={ci.id} item={ci} onAdd={(qty) => onAddFromCatalog(ci, qty)} />
                ))}
              </div>
            )}
            </>
            )}
          </div>
        )}

        {tab === "ki" && (
          <div className="space-y-4">
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              Fügen Sie ein altes Angebot oder eine Positionsliste ein. Die KI erstellt einen
              <strong> Entwurf</strong> – nichts wird automatisch gespeichert, Sie prüfen zuerst.
            </p>
            <Textarea
              className="min-h-[160px]"
              placeholder="z. B. „Menü Klassisch 45,00 € pro Person\nGetränkepauschale 25,00 € pro Person\nGesamt 2.800,00 €"
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
            />
            {importResult && (
              <div className="rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 dark:border-outline/30 dark:bg-surface-variant/10">
                {importResult.warning === "nicht_parsebar" ? (
                  <p className="font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">Kein strukturierter Inhalt erkannt – bitte Text prüfen.</p>
                ) : (
                  <>
                    <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
                      <strong>{importResult.count}</strong> Positionen übernommen · berechnet {money(importResult.computedTotalCents ?? 0)}
                      {importResult.statedTotalCents != null && ` · im Text: ${money(importResult.statedTotalCents)}`}
                    </p>
                    {importResult.flags.map((f, i) => (
                      <p key={i} className="mt-1 flex items-center gap-1.5 font-body-sm text-body-sm text-secondary dark:text-[#b8ae9e]">
                        <Icon name="warning" className="text-[14px]" /> {f.message}
                      </p>
                    ))}
                  </>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={onClose}>
                {importResult && importResult.count > 0 ? "Fertig" : "Abbrechen"}
              </Button>
              <Button type="button" variant="primary" disabled={importBusy || !importText.trim()} onClick={() => void handleParse()}>
                {importBusy ? "Analysiert …" : "Analysieren & übernehmen"}
              </Button>
            </div>
          </div>
        )}

        {tab === "manuell" && (
          <div className="space-y-4">
            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              Fügt eine leere Position in der Sektion „{SECTIONS.find((s) => s.key === section)?.label}" hinzu — Bezeichnung, Menge und
              Preis tragen Sie danach direkt in der Zeile ein.
            </p>
            <Button
              type="button"
              variant="primary"
              className="w-full"
              onClick={() => {
                onAddFreetext(section);
                onClose();
              }}
            >
              <Icon name="add" className="text-[18px]" /> Leere Zeile hinzufügen
            </Button>
          </div>
        )}
      </div>
      {paketFrage &&
        createPortal(
          <Modal
            open
            oben
            width="440px"
            onClose={() => setPaketFrage(null)}
            title={`„${paketFrage.name}" übernehmen`}
            footer={
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void onApplyPackage?.(paketFrage);
                    setPaketFrage(null);
                    onClose();
                  }}
                >
                  Als neue Variante
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  autoFocus
                  onClick={() => {
                    void onApplyPackageInAktive?.(paketFrage);
                    setPaketFrage(null);
                    onClose();
                  }}
                >
                  In offene Variante
                </Button>
              </>
            }
          >
            <p className="font-body-sm text-body-sm text-on-background dark:text-inverse-on-surface">
              Sollen die Positionen in die offene Variante „{offeneVariante}" kommen oder als eigene, neue Variante angelegt
              werden?
            </p>
          </Modal>,
          document.body,
        )}
    </Drawer>
  );
}
